import { apiRequest } from '../lib/api-client';

/** Account, access and the audit trail. */

export interface SessionRow {
  id: string;
  current: boolean;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
  last_used_at: string | null;
  expires_at: string;
}

export function listSessions(): Promise<{ sessions: SessionRow[] }> {
  return apiRequest('/v1/auth/sessions');
}

export function revokeSession(id: string): Promise<void> {
  return apiRequest(`/v1/auth/sessions/${id}`, { method: 'DELETE' });
}

/**
 * Asks for another confirmation e-mail (Phase 128).
 *
 * Needs the session, which is the point: an endpoint that mails anybody on request is an endpoint
 * that mails anybody on request. The server rate-limits it to ten an hour per person.
 */
export function resendVerification(): Promise<{ sent: boolean }> {
  return apiRequest('/v1/auth/email/resend', { method: 'POST' });
}

export function changePassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ revoked_sessions: number }> {
  return apiRequest('/v1/auth/password/change', { method: 'POST', body: input });
}

export interface RoleRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  is_system: boolean;
  permissions: { key: string; scope: string }[];
}

export function listRoles(): Promise<{ roles: RoleRow[] }> {
  return apiRequest('/v1/access/roles');
}

export interface TeamRow {
  id: string;
  name: string;
  parent_id: string | null;
  member_count?: number;
}

export function listTeams(): Promise<{ teams: TeamRow[] }> {
  return apiRequest('/v1/access/teams');
}

export interface AuditEvent {
  id: string;
  actor_type: string;
  actor_id: string | null;
  operator_id: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Record<string, unknown>;
  ip: string | null;
  request_id: string | null;
  occurred_at: string;
}

export interface AuditFilters {
  action?: string;
  entityType?: string;
  before?: string;
}

export function listAuditEvents(
  filters: AuditFilters = {},
): Promise<{ events: AuditEvent[]; next_cursor: string | null }> {
  const params = new URLSearchParams({ limit: '100' });

  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }

  return apiRequest(`/v1/audit/events?${params.toString()}`);
}

export interface Entitlements {
  plan_key: string;
  plan_name: string;
  status: string;
  trial_ends_at: string | null;
  current_period_end: string | null;
  grace_ends_at: string | null;
  read_only: boolean;
  features: string[];
  limits: Record<string, number>;
}

export function getEntitlements(): Promise<Entitlements> {
  return apiRequest('/v1/entitlements');
}

export interface AvatarResult {
  file_id: string;
  url: string;
  expires_at: string;
}

/** Uploads an already-cropped square image as the profile picture. */
export function saveAvatar(input: {
  filename: string;
  mimeType: string;
  content: string;
}): Promise<AvatarResult> {
  return apiRequest('/v1/auth/avatar', { method: 'PUT', body: input });
}

export function removeAvatar(): Promise<void> {
  return apiRequest('/v1/auth/avatar', { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// The team

export interface MemberView {
  membership_id: string;
  user_id: string;
  email: string;
  name: string;
  role_key: string;
  status: 'ACTIVE' | 'INVITED' | 'SUSPENDED' | 'REMOVED';
  invited_by: string | null;
  joined_at: string | null;
  /** When an outstanding invitation stops working; null once accepted. */
  expires_at: string | null;
  created_at: string;
  /** A short-lived URL for this person's picture, or null when they have none this tenant can see. */
  avatar_url: string | null;
}

export function listMembers(): Promise<{ members: MemberView[] }> {
  return apiRequest('/v1/access/members');
}

export function inviteMember(input: {
  email: string;
  name?: string;
  roleKey: string;
}): Promise<MemberView> {
  return apiRequest('/v1/access/members', { method: 'POST', body: input });
}

export function changeMemberRole(
  membershipId: string,
  roleKey: string,
): Promise<{ membershipId: string; roleKey: string }> {
  return apiRequest(`/v1/access/members/${membershipId}/role`, {
    method: 'PUT',
    body: { roleKey },
  });
}

export function removeMember(membershipId: string): Promise<void> {
  return apiRequest(`/v1/access/members/${membershipId}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Invoices

export type InvoiceStatus = 'DRAFT' | 'OPEN' | 'PAID' | 'VOID' | 'UNCOLLECTIBLE';

export interface InvoiceView {
  id: string;
  number: number;
  status: InvoiceStatus;
  plan_key: string;
  amount_cents: number;
  currency: string;
  period_start: string;
  period_end: string;
  due_at: string;
  paid_at: string | null;
  payment_method: string | null;
  provider: string;
}

export function listInvoices(): Promise<{ invoices: InvoiceView[] }> {
  return apiRequest('/v1/billing/invoices?limit=24');
}

// ---------------------------------------------------------------------------
// Usage (Phase 32)
// ---------------------------------------------------------------------------

export type HealthBand = 'NEW' | 'THRIVING' | 'HEALTHY' | 'QUIET' | 'DORMANT';

export interface AccountUsage {
  windowDays: number;
  health: {
    score: number;
    band: HealthBand;
    parts: { activity: number; breadth: number; people: number };
  };
  activation: {
    reached: string[];
    missing: string[];
    activated: boolean;
    activatedAt: string | null;
  };
  activeUsers: number;
  members: number;
  activeDays: number;
  events: number;
  lastEventAt: string | null;
  byArea: { area: string; events: number; users: number }[];
  byDay: { day: string; events: number }[];
  users: { id: string; name: string; events: number; areas: number; lastActiveAt: string | null }[];
}

export function accountUsage(): Promise<AccountUsage> {
  return apiRequest('/v1/analytics/usage');
}

// ---------------------------------------------------------------------------
// Integrations: API keys and webhooks
// ---------------------------------------------------------------------------

export interface ApiKeyRow {
  id: string;
  name: string;
  prefix: string;
  scopes: string[] | null;
  createdBy: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export function listApiKeys(): Promise<{ keys: ApiKeyRow[] }> {
  return apiRequest('/v1/integrations/api-keys');
}

export function createApiKey(input: {
  name: string;
  scopes?: string[] | null;
  expiresAt?: string | null;
}): Promise<ApiKeyRow & { key: string }> {
  return apiRequest('/v1/integrations/api-keys', { method: 'POST', body: input });
}

export function revokeApiKey(id: string): Promise<ApiKeyRow> {
  return apiRequest(`/v1/integrations/api-keys/${id}`, { method: 'DELETE' });
}

export interface WebhookRow {
  id: string;
  name: string;
  url: string;
  events: string[];
  active: boolean;
  failures: number;
  disabledAt: string | null;
  disabledReason: string | null;
  createdAt: string;
}

export interface DeliveryRow {
  id: string;
  eventId: string;
  eventType: string;
  status: 'QUEUED' | 'SENDING' | 'DELIVERED' | 'FAILED' | 'DEAD';
  attempts: number;
  nextAttemptAt: string;
  responseStatus: number | null;
  responseBody: string | null;
  lastError: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

export function listWebhooks(): Promise<{ subscriptions: WebhookRow[] }> {
  return apiRequest('/v1/integrations/webhooks');
}

export function createWebhook(input: {
  name: string;
  url: string;
  events: string[];
}): Promise<WebhookRow & { secret: string }> {
  return apiRequest('/v1/integrations/webhooks', { method: 'POST', body: input });
}

export function patchWebhook(
  id: string,
  input: { name?: string; url?: string; events?: string[]; active?: boolean },
): Promise<WebhookRow> {
  return apiRequest(`/v1/integrations/webhooks/${id}`, { method: 'PATCH', body: input });
}

export function deleteWebhook(id: string): Promise<void> {
  return apiRequest(`/v1/integrations/webhooks/${id}`, { method: 'DELETE' });
}

export function listDeliveries(id: string): Promise<{ deliveries: DeliveryRow[] }> {
  return apiRequest(`/v1/integrations/webhooks/${id}/deliveries?limit=50`);
}

export function retryDelivery(id: string): Promise<DeliveryRow> {
  return apiRequest(`/v1/integrations/deliveries/${id}/retry`, { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Changing plan, without asking anybody (Phase 49)
// ---------------------------------------------------------------------------

export type ChangeKind = 'UPGRADE' | 'DOWNGRADE' | 'LATERAL';

export interface ChangeQuote {
  kind: ChangeKind;
  effective: 'NOW' | 'PERIOD_END';
  chargeCents: number;
  creditCents: number;
  nextPeriodCents: number;
}

export interface PlanBlocker {
  key: string;
  limit: number;
  current: number;
}

export interface PlanOffer {
  key: string;
  name: string;
  description: string;
  priceCents: number;
  currency: string;
  billingPeriod: string;
  features: string[];
  limits: Record<string, number | null>;
  current: boolean;
  scheduled: boolean;
  quote: ChangeQuote | null;
  blockers: PlanBlocker[];
}

export interface PlanCatalogue {
  currentPlanKey: string;
  status: string;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  scheduledPlanKey: string | null;
  scheduledChangeAt: string | null;
  usage: Record<string, number>;
  plans: PlanOffer[];
}

export function planCatalogue(): Promise<PlanCatalogue> {
  return apiRequest('/v1/billing/plans');
}

export interface ChangeResult {
  planKey: string;
  quote: ChangeQuote;
  effectiveAt: string;
  invoiceId: string | null;
  invoiceAmountCents: number;
}

export function changePlan(planKey: string): Promise<ChangeResult> {
  return apiRequest('/v1/billing/plan', { method: 'POST', body: { planKey } });
}

export function cancelScheduledPlanChange(): Promise<void> {
  return apiRequest('/v1/billing/plan/scheduled', { method: 'DELETE' });
}
